import { F } from '@/utils/intl';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'umi';
import { Button, Flex, GetProp, Image, message, Spin, Tooltip, Upload, UploadFile, UploadProps } from 'antd';
import { DeleteOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { getAuthorizationHeader } from '@/services/auth';
import { getAPIErrorMessage } from '@/utils/request';
import styles from './index.module.less';
import classNames from 'classnames';

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const getBase64 = (file: FileType): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

export interface ThumbnailUploadProps {
  currentThumbnail?: string;
  uploadUrl: string;
  name?: string;
  onDelete?: () => Promise<any>;
  onUploadSuccess?: () => void;
  onUploadError?: () => void;
  onDeleteSuccess?: () => void;
  onDeleteError?: () => void;
  maxCount?: number;
  accept?: string;
  deleteButtonClassName?: string;
  /** Shows a preview button beside the delete one (the hover eye alone is easy to miss). */
  showPreviewButton?: boolean;
}

const ThumbnailUpload: React.FC<ThumbnailUploadProps> = ({
                                                           currentThumbnail,
                                                           uploadUrl,
                                                           name = 'thumbnail',
                                                           onDelete,
                                                           onUploadSuccess,
                                                           onUploadError,
                                                           onDeleteSuccess,
                                                           onDeleteError,
                                                           maxCount = 1,
                                                           deleteButtonClassName,
                                                           accept = 'image/*',
                                                           showPreviewButton = false,
                                                         }) => {
  const intl = useIntl();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(false);

  useEffect(() => {
    if (currentThumbnail) {
      setDeleted(false);
    }
  }, [currentThumbnail]);

  useEffect(() => {
    // Reset file list when component unmounts
    return () => {
      setFileList([]);
      setPreviewOpen(false);
      setPreviewImage('');
    };
  }, []);

  // The card's file: the fresh upload, else the saved thumbnail.
  const shownFile: UploadFile = fileList[0] || { uid: '-1', name: 'thumbnail', url: currentThumbnail };

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as FileType);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  const handleUploadChange: UploadProps['onChange'] = ({ fileList: newFileList, file }) => {
    setFileList(newFileList);
    setDeleted(false);
    // When upload is complete, trigger success callback
    if (file.status === 'done') {
      if (onUploadSuccess) {
        onUploadSuccess();
      }
      message.success(intl.formatMessage({
        id: 'pages.serviceTypes.thumbnailUploadSuccess',
        defaultMessage: 'Thumbnail uploaded successfully',
      }));
    } else if (file.status === 'error') {
      // Drop the failed file so the card falls back to the saved thumbnail (and delete can't hit it).
      setFileList([]);
      if (onUploadError) {
        onUploadError();
      }
      message.error(getAPIErrorMessage(file.response, intl.formatMessage({
        id: 'pages.serviceTypes.thumbnailUploadError',
        defaultMessage: 'Failed to upload thumbnail',
      })));
    }
  };

  const handleRemove: UploadProps['onRemove'] = async () => {
    if (!onDelete) return false;
    setDeleting(true);
    try {
      await onDelete();

      setFileList([]);

      if (onDeleteSuccess) {
        onDeleteSuccess();
      }

      message.success(intl.formatMessage({
        id: 'pages.serviceTypes.thumbnailDeleteSuccess',
        defaultMessage: 'Thumbnail deleted successfully',
      }));
      setDeleted(true);
      setDeleting(false);
      return true;
    } catch (error) {
      if (onDeleteError) {
        onDeleteError();
      }

      message.error(intl.formatMessage({
        id: 'pages.serviceTypes.thumbnailDeleteError',
        defaultMessage: 'Failed to delete thumbnail',
      }));

      setDeleting(false);
      return false;
    }
  };

  return (
    <Spin spinning={deleting}>
      <div className={styles.uploadWrapper}>
        <Upload
          listType="picture-card"
          name={name}
          fileList={
            currentThumbnail && fileList.length === 0 && !deleted
              ? [
                  {
                    uid: '-1',
                    name: 'thumbnail',
                    status: 'done',
                    url: currentThumbnail,
                  },
                ]
              : fileList
          }
          onPreview={handlePreview}
          onChange={handleUploadChange}
          maxCount={maxCount}
          accept={accept}
          showUploadList={{
            showRemoveIcon: false,
          }}
          headers={getAuthorizationHeader()}
          action={uploadUrl}
        >
          {fileList.length === 0 && (!currentThumbnail || deleted) && (
            <button
              style={{ border: 0, background: 'none', cursor: 'pointer' }}
              type="button"
            >
              <PlusOutlined />
              <div style={{ marginTop: 8 }}>
                <F id="common.actions.upload" defaultMessage="Upload" />
              </div>
            </button>
          )}
        </Upload>
        {(fileList.length > 0 || currentThumbnail) && !deleted && (showPreviewButton ? (
          <Flex gap={4} className={styles.sideActions}>
            <Tooltip title={<F id="pages.common.preview" defaultMessage="Preview" />}>
              <Button
                onClick={() => handlePreview(shownFile)}
                size={'small'}
                icon={<EyeOutlined />}
                variant={'filled'}
                color={'default'}
              />
            </Tooltip>
            <Tooltip title={<F id="common.actions.delete" defaultMessage="Delete" />}>
              <Button
                onClick={() => handleRemove(shownFile)}
                className={deleteButtonClassName}
                size={'small'}
                icon={<DeleteOutlined />}
                variant={'filled'}
                color={'danger'}
                danger
              />
            </Tooltip>
          </Flex>
        ) : (
          <Button
            onClick={handleRemove}
            className={classNames(styles.deleteButton, deleteButtonClassName)}
            size={'small'}
            icon={<DeleteOutlined />}
            variant={'filled'}
            color={'danger'}
            danger
          />
        ))}
      </div>
      {previewImage && (
        <Image
          wrapperStyle={{ display: 'none' }}
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) => !visible && setPreviewImage(''),
          }}
          src={previewImage}
        />
      )}
    </Spin>
  );
};

export default ThumbnailUpload;
